import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Stack, Typography, InputBase } from '@mui/material';
import { CheckCircle, RadioButtonUnchecked, Search, PlayArrowRounded } from '@mui/icons-material';
import AyudaShell from './AyudaShell';
import { useCursoProgress } from './cursoProgress';
import { useHeadMeta } from './useHeadMeta';
import { CURSO_MINUTOS, LECCIONES, MODULOS, fmtDuracion, leccionCoincide } from './curso';
import { CREAM_SOFT, DISPLAY, INDIGO, INK, INK_MUTED, MONO, RULE, RULE_HARD, SANS, YELLOW, YELLOW_DEEP } from '../../components/public/publicTokens';

const TOTAL = LECCIONES.length;

const ProgressCard: React.FC<{ vistas: number; nextSlug: string | null; empezado: boolean }> = ({ vistas, nextSlug, empezado }) => {
  const pct = TOTAL ? Math.round((vistas / TOTAL) * 100) : 0;
  return (
    <Box sx={{ bgcolor: CREAM_SOFT, border: `1px solid ${RULE_HARD}`, borderRadius: '16px', p: 3 }}>
      <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: INK_MUTED, mb: 1 }}>
        Tu progreso
      </Typography>
      <Typography sx={{ fontFamily: DISPLAY, fontSize: '2rem', lineHeight: 1.1 }}>
        {vistas} <Box component="span" sx={{ color: INK_MUTED, fontSize: '1.3rem' }}>de {TOTAL} lecciones</Box>
      </Typography>
      <Box
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={TOTAL}
        aria-valuenow={vistas}
        aria-label="Lecciones vistas"
        sx={{ mt: 2, height: 8, borderRadius: 99, bgcolor: 'rgba(10,10,10,0.08)', overflow: 'hidden' }}
      >
        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: YELLOW_DEEP, transition: 'width 0.4s ease' }} />
      </Box>
      {nextSlug ? (
        <Box
          component={RouterLink}
          to={`/ayuda/${nextSlug}`}
          sx={{
            mt: 2.5,
            display: 'block',
            textAlign: 'center',
            bgcolor: INK,
            color: CREAM_SOFT,
            borderRadius: 999,
            py: 1.3,
            fontFamily: SANS,
            fontWeight: 600,
            fontSize: '0.92rem',
            textDecoration: 'none',
            '&:hover': { bgcolor: INDIGO },
          }}
        >
          {empezado ? 'Seguir donde quedé →' : 'Empezar el curso →'}
        </Box>
      ) : (
        <Typography sx={{ mt: 2.5, fontFamily: SANS, fontWeight: 600 }}>Terminaste el curso. ¡Bien ahí!</Typography>
      )}
      <Typography sx={{ mt: 1.5, fontFamily: SANS, fontSize: '0.78rem', color: INK_MUTED }}>
        El progreso se guarda en este navegador.
      </Typography>
    </Box>
  );
};

const AyudaIndexPage: React.FC = () => {
  const { vistas } = useCursoProgress();
  const [q, setQ] = useState('');

  useHeadMeta({
    title: 'Centro de ayuda de DashFlow: curso paso a paso',
    description: `Aprendé DashFlow de principio a fin: ${MODULOS.length} módulos y ${TOTAL} lecciones cortas para vender, controlar el stock, cobrar y facturar en tu comercio.`,
    path: '/ayuda',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Curso de DashFlow',
      itemListElement: LECCIONES.map((l) => ({
        '@type': 'ListItem',
        position: l.numero,
        name: l.titulo,
        url: `https://dashflow.com.ar/ayuda/${l.slug}`,
      })),
    },
  });

  const vistasCount = LECCIONES.filter((l) => vistas.has(l.slug)).length;
  const next = LECCIONES.find((l) => !vistas.has(l.slug)) || null;

  const filtradas = useMemo(() => {
    const term = q.trim();
    if (!term) return null;
    return new Set(LECCIONES.filter((l) => leccionCoincide(l, term)).map((l) => l.slug));
  }, [q]);

  return (
    <AyudaShell>
      <Container maxWidth="lg" sx={{ px: { xs: 2, md: 3 }, pt: { xs: 5, md: 9 }, pb: { xs: 8, md: 12 } }}>
        {/* Hero */}
        <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: INK_MUTED }}>
          Centro de ayuda · Curso
        </Typography>
        <Typography component="h1" sx={{ fontFamily: DISPLAY, fontWeight: 400, fontSize: { xs: '2.6rem', md: '4.2rem' }, lineHeight: 1.02, mt: 1.5, maxWidth: 820 }}>
          Aprendé{' '}
          <Box component="span" sx={{ fontStyle: 'italic', background: `linear-gradient(transparent 62%, ${YELLOW} 62%)` }}>
            DashFlow
          </Box>{' '}
          de principio a fin.
        </Typography>
        <Typography sx={{ fontFamily: SANS, fontSize: { xs: '1rem', md: '1.1rem' }, color: INK_MUTED, mt: 2, maxWidth: 640 }}>
          Cada lección es una tarea, con los mismos nombres de botones y pantallas que vas a ver en el sistema.
        </Typography>
        <Stack direction="row" spacing={{ xs: 3, md: 5 }} sx={{ mt: 4 }}>
          {[
            { n: MODULOS.length, l: 'módulos' },
            { n: TOTAL, l: 'lecciones' },
            ...(CURSO_MINUTOS > 0 ? [{ n: CURSO_MINUTOS, l: 'minutos en total' }] : []),
          ].map((c) => (
            <Box key={c.l}>
              <Typography sx={{ fontFamily: DISPLAY, fontSize: { xs: '2.2rem', md: '2.8rem' }, lineHeight: 1 }}>{c.n}</Typography>
              <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: INK_MUTED }}>{c.l}</Typography>
            </Box>
          ))}
        </Stack>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) 320px' }, gap: { xs: 4, md: 6 }, mt: { xs: 5, md: 7 }, alignItems: 'start' }}>
          {/* Progreso: arriba en mobile, sticky a la derecha en desktop */}
          <Box sx={{ order: { xs: 0, md: 2 }, position: { md: 'sticky' }, top: { md: 96 } }}>
            <ProgressCard vistas={vistasCount} nextSlug={next?.slug ?? null} empezado={vistasCount > 0} />
          </Box>

          <Box sx={{ order: 1, minWidth: 0 }}>
            {/* Buscador */}
            <Box
              component="label"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2.2,
                py: 1.4,
                bgcolor: CREAM_SOFT,
                border: `1px solid ${RULE_HARD}`,
                borderRadius: '14px',
                '&:focus-within': { borderColor: INK },
              }}
            >
              <Search sx={{ color: INK_MUTED }} />
              <InputBase
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="¿Qué querés hacer? Ej.: cargar un producto, cerrar la caja"
                inputProps={{ 'aria-label': '¿Qué querés hacer?' }}
                sx={{ flex: 1, fontFamily: SANS, fontSize: '1rem' }}
              />
            </Box>
            {filtradas && (
              <Typography sx={{ mt: 1.5, fontFamily: SANS, fontSize: '0.88rem', color: INK_MUTED }} aria-live="polite">
                {filtradas.size === 0
                  ? 'No encontramos lecciones con esa búsqueda. Probá con otra palabra o escribinos por WhatsApp.'
                  : `${filtradas.size} ${filtradas.size === 1 ? 'lección' : 'lecciones'}`}
              </Typography>
            )}

            {/* Módulos */}
            <Stack spacing={{ xs: 4, md: 5 }} sx={{ mt: 4 }}>
              {MODULOS.map((m, mi) => {
                const lecciones = LECCIONES.filter((l) => l.moduloIndex === mi);
                const visibles = filtradas ? lecciones.filter((l) => filtradas.has(l.slug)) : lecciones;
                if (visibles.length === 0) return null;
                const vistasMod = lecciones.filter((l) => vistas.has(l.slug)).length;
                return (
                  <Box component="section" key={m.id} aria-labelledby={`mod-${m.id}`}>
                    <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={2} sx={{ pb: 1.5, borderBottom: `1px solid ${RULE_HARD}` }}>
                      <Box>
                        <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: INK_MUTED }}>
                          Módulo {mi + 1}
                        </Typography>
                        <Typography id={`mod-${m.id}`} component="h2" sx={{ fontFamily: DISPLAY, fontSize: { xs: '1.7rem', md: '2rem' }, lineHeight: 1.1 }}>
                          {m.nombre}
                        </Typography>
                        <Typography sx={{ fontFamily: SANS, fontSize: '0.92rem', color: INK_MUTED }}>{m.bajada}</Typography>
                      </Box>
                      <Typography sx={{ fontFamily: MONO, fontSize: '0.75rem', color: INK_MUTED, whiteSpace: 'nowrap' }}>
                        {vistasMod} de {lecciones.length}
                      </Typography>
                    </Stack>
                    <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                      {visibles.map((l) => {
                        const vista = vistas.has(l.slug);
                        const sigue = !filtradas && next?.slug === l.slug;
                        return (
                          <Box component="li" key={l.slug} sx={{ borderBottom: `1px solid ${RULE}` }}>
                            <Box
                              component={RouterLink}
                              to={`/ayuda/${l.slug}`}
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: '28px 36px minmax(0,1fr) auto',
                                alignItems: 'start',
                                gap: 1,
                                py: 2,
                                px: 1,
                                color: INK,
                                textDecoration: 'none',
                                borderRadius: '10px',
                                '&:hover': { bgcolor: 'rgba(10,10,10,0.035)' },
                              }}
                            >
                              {vista ? (
                                <CheckCircle sx={{ color: '#15803d', fontSize: 22, mt: '2px' }} aria-label="Vista" />
                              ) : (
                                <RadioButtonUnchecked sx={{ color: 'rgba(10,10,10,0.3)', fontSize: 22, mt: '2px' }} aria-label="Sin ver" />
                              )}
                              <Typography sx={{ fontFamily: MONO, fontSize: '0.85rem', color: INK_MUTED, mt: '3px' }}>{l.numero}</Typography>
                              <Box sx={{ minWidth: 0 }}>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                  <Typography component="h3" sx={{ fontFamily: SANS, fontWeight: 600, fontSize: '1rem' }}>
                                    {l.titulo}
                                  </Typography>
                                  {sigue && (
                                    <Box component="span" sx={{ fontFamily: MONO, fontSize: '0.65rem', letterSpacing: '0.12em', textTransform: 'uppercase', bgcolor: YELLOW, px: 1, py: 0.2, borderRadius: 99 }}>
                                      Sigue acá
                                    </Box>
                                  )}
                                  {l.plan === 'pago' && (
                                    <Box component="span" sx={{ fontFamily: MONO, fontSize: '0.65rem', letterSpacing: '0.1em', textTransform: 'uppercase', border: `1px solid ${RULE_HARD}`, color: INK_MUTED, px: 1, py: 0.1, borderRadius: 99 }}>
                                      Plan pago
                                    </Box>
                                  )}
                                </Stack>
                                <Typography sx={{ fontFamily: SANS, fontSize: '0.9rem', color: INK_MUTED, mt: 0.3 }}>{l.descripcion}</Typography>
                              </Box>
                              {l.video ? (
                                <Stack direction="row" alignItems="center" sx={{ color: INK_MUTED, mt: '2px' }}>
                                  <PlayArrowRounded sx={{ fontSize: 18 }} />
                                  <Typography sx={{ fontFamily: MONO, fontSize: '0.8rem' }}>{fmtDuracion(l.video.duracion)}</Typography>
                                </Stack>
                              ) : (
                                <span />
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Box>
      </Container>
    </AyudaShell>
  );
};

export default AyudaIndexPage;
